import React from "react";
import { Link } from "react-router-dom";
import { Heart, Leaf, Sparkles, MapPin, ShieldCheck, Palette } from "lucide-react";
import { brand } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { Reveal } from "@/components/Reveal";
import { Blob } from "@/components/Deco";
import BookCta from "@/components/BookCta";

const values = [
  { icon: Heart, title: "Patient & kind", text: "Gentle and encouraging with shy little ones and wiggly toddlers alike." },
  { icon: Sparkles, title: "Creative at heart", text: "From tiny tigers to sparkling butterflies — and plenty of wild ideas in between." },
  { icon: ShieldCheck, title: "Licensed & insured", text: "A professional, insured business schools and venues can book with confidence." },
  { icon: Leaf, title: "Rooted in Nanaimo", text: "A proud local who loves this island community and its markets and events." },
];

export default function About() {
  useSeo({ title: "About Tay — Nanaimo Face Painter", description: "Meet Tayler Kultgen, the friendly, licensed and insured face painter behind Paint With Tay in Nanaimo." });

  return (
    <div data-testid="about-page">
      <section className="section py-14 grid lg:grid-cols-2 gap-10 items-center">
        <Reveal className="relative">
          <Blob className="absolute -top-8 -left-8 w-40 h-40 opacity-40 animate-float" color="#FFD447" />
          <div className="relative card-pop p-4 bg-cream">
            <img src={brand.mascot} alt="Illustration of Tayler in grey overalls holding a palette and brushes" className="w-full max-w-sm mx-auto" />
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="font-accent text-3xl text-hotpink">Happy faces are kind of Tay’s thing.</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-700 mt-1 mb-5">Hi, I’m Tayler.</h1>
          <div className="space-y-4 text-lg text-ink/90 font-body">
            <p>I’m the face, hands, and happy heart behind Paint With Tay. I’m a Nanaimo-based artist who genuinely loves colour, creativity, and the joy on someone’s face when they see themselves transformed into a tiger, a butterfly, or their very own wild idea.</p>
            <p>You’ll usually spot me in my trusty grey overalls and comfy boots, palette in hand, chatting away with kids and grown-ups. I’m patient, friendly, and I take real care to make every guest — no matter their age — feel comfortable and special.</p>
            <p>Paint With Tay is a licensed and insured business, so whether you’re planning a backyard birthday, a busy market, a school celebration, or a wedding, you can relax knowing you’re in good hands. Let’s add a little colour to your celebration!</p>
          </div>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/book" className="btn-pink" data-testid="about-book-btn"><Palette className="w-5 h-5" /> Book Paint With Tay</Link>
            <a href={brand.instagram} target="_blank" rel="noopener noreferrer" className="btn-blue">See my work</a>
          </div>
        </Reveal>
      </section>

      <section className="section pb-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {values.map((v, i) => (
          <Reveal key={v.title} delay={i * 0.08}>
            <div className="card-soft p-6 h-full">
              <div className="w-12 h-12 rounded-full bg-sunshine border-[3px] border-ink flex items-center justify-center mb-3">
                <v.icon className="w-6 h-6 text-ink" />
              </div>
              <h3 className="font-heading text-xl font-600 mb-1">{v.title}</h3>
              <p className="text-ink/80 font-body">{v.text}</p>
            </div>
          </Reveal>
        ))}
      </section>

      <BookCta />
    </div>
  );
}
